import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Cpu, HardDrive, Monitor, Clock, Server } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ServerStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/server-status`);
        setStatus(response.data);
      } catch (err) {
        setError(err.message || "Failed to fetch server status");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-purple animate-pulse">Loading server status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Server className="w-16 h-16 text-purple mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-white mb-2">Server Status</h1>
          <p className="text-slate-400">System Specifications & Diagnostics</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* RAM Card */}
          <Card className="bg-slate-900/50 border-slate-800" data-testid="ram-card">
            <CardHeader className="flex flex-row items-center space-x-4">
              <HardDrive className="w-8 h-8 text-purple" />
              <CardTitle className="text-white">Total RAM</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple" data-testid="ram-value">
                {typeof status.ram_total_gb === 'number' 
                  ? `${status.ram_total_gb} GB` 
                  : status.ram_total_gb}
              </p>
              <p className="text-slate-400 mt-2">Available system memory</p>
            </CardContent>
          </Card>

          {/* CPU Card */}
          <Card className="bg-slate-900/50 border-slate-800" data-testid="cpu-card">
            <CardHeader className="flex flex-row items-center space-x-4">
              <Cpu className="w-8 h-8 text-purple" />
              <CardTitle className="text-white">CPU Cores</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple" data-testid="cpu-value">
                {status.cpu_cores}
              </p>
              <p className="text-slate-400 mt-2">Available processing cores</p>
            </CardContent>
          </Card>

          {/* OS Info Card */}
          <Card className="bg-slate-900/50 border-slate-800 md:col-span-2" data-testid="os-card">
            <CardHeader className="flex flex-row items-center space-x-4">
              <Monitor className="w-8 h-8 text-purple" />
              <CardTitle className="text-white">Operating System</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">System</p>
                  <p className="text-white font-medium" data-testid="os-system">
                    {status.os_info.system}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Release</p>
                  <p className="text-white font-medium" data-testid="os-release">
                    {status.os_info.release}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Version</p>
                  <p className="text-white font-medium text-sm" data-testid="os-version">
                    {status.os_info.version}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Machine</p>
                  <p className="text-white font-medium" data-testid="os-machine">
                    {status.os_info.machine}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Processor</p>
                  <p className="text-white font-medium" data-testid="os-processor">
                    {status.os_info.processor || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Python Version</p>
                  <p className="text-white font-medium" data-testid="python-version">
                    {status.os_info.python_version}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timestamp Card */}
          <Card className="bg-slate-900/50 border-slate-800 md:col-span-2" data-testid="timestamp-card">
            <CardHeader className="flex flex-row items-center space-x-4">
              <Clock className="w-8 h-8 text-purple" />
              <CardTitle className="text-white">Data Retrieved At</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white font-mono" data-testid="timestamp-value">
                {new Date(status.timestamp).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            This is a secret diagnostics page. Data is fetched directly from the server.
          </p>
        </div>
      </div>
    </div>
  );
}
